/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";
import { Card } from "@material-ui/core";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { getContactFlowsAsync } from "../../../../sharedServices/AmazonConnectService";
import { AzureIntegrationAppCard } from "./AzureIntegrationAppCard";
import {
	getThirdPartyApplications,
	updateThirdPartyApplications,
	deleteThirdPartyApplication,
} from "../../services/ThirdPartyService";
import { v4 as uuid } from "uuid";
import { ApplicationList } from "./ApplicationList";
import { THIRD_PARTY_APPLICATION_TYPES } from "../../../../utility/constants";
import { LeftPanelLayout, RightPanelLayout } from "../../../../components/Common";
import { EmptyPlaceholder } from "../../../../components";

const COMPONENT_NAME = "Admin SettingsManager/SocialMediaIntegrationSection";

/**
 * This component houses fields for Third party Azure application integration
 * @param {(Boolean) => void} setFormEdited Callback method for setting the form as edited.
 * @param {Boolean} formEdited A boolean value denoting whether the form has been edited or not.
 * @param {(Boolean) => void} setSaving Callback method for setting the form as saving.
 * @param {Boolean} saving A boolean value denoting whether the form is saving.
 * @param {(Boolean) => void} setPageLoading Callback method for setting the page as loading.
 */
export const AzureBotSection = ({ showSnackbar, setFormEdited, formEdited, setSaving, saving, setPageLoading }) => {
	const [appConfigs, setAppConfigs] = React.useState(undefined);
	const [filteredAppConfigList, setFilteredAppConfigsList] = React.useState(undefined);
	const [selectedConfig, setSelectedConfig] = React.useState(undefined);
	const [selectedConfigId, setSelectedConfigId] = React.useState(undefined);
	const [loading, setFormLoading] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [contactFlows, setContactFlows] = React.useState(undefined);
	const [saveDisabled, setSaveDisabled] = React.useState(false);

	const setLoading = React.useCallback(
		(newLoading) => {
			setPageLoading(newLoading);
			setFormLoading(newLoading);
		},
		[setPageLoading],
	);

	React.useEffect(() => {
		const fetchAppConfigs = async () => {
			try {
				const applications = await getThirdPartyApplications(THIRD_PARTY_APPLICATION_TYPES.AZURE);
				//get contact flows
				try {
					if (!contactFlows) {
						const flows = await getContactFlowsAsync();
						setContactFlows(flows);
					}
				} catch (error) {
					v2.error(`Error while fetching contact flows.`, {
						additionalData: { error: errorToObject(error) },
						componentName: COMPONENT_NAME,
						logCode: errorCodes.GET_CONTACT_FLOWS_API_ERROR,
					});
				}
				setAppConfigs(applications);
				setFilteredAppConfigsList(
					Object.values(applications).sort((a, b) =>
						a.friendlyName.toLowerCase() > b.friendlyName.toLowerCase() ? 1 : -1,
					),
				);
			} catch (error) {
				v2.error(`Error while fetching social media applications.`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_THIRD_PARTY_APPLICATIONS_ERROR,
				});
				setAppConfigs({});
				setFilteredAppConfigsList([]);
			} finally {
				setLoading(false);
			}
		};
		if (!appConfigs && !loading) {
			setLoading(true);
			fetchAppConfigs();
		}
	}, [loading, setLoading, appConfigs, contactFlows]);

	const onSearchTermChange = React.useCallback((newSearchTerm, appConfigs) => {
		const newfilteredConfigsList = [];
		const searchTerm = newSearchTerm.toLowerCase();
		Object.values(appConfigs).forEach((config) => {
			if (config.botId.toLowerCase().includes(searchTerm) || config.friendlyName.toLowerCase().includes(searchTerm)) {
				newfilteredConfigsList.push(config);
			}
		});
		setSearchTerm(newSearchTerm);
		setFilteredAppConfigsList(
			newfilteredConfigsList.sort((a, b) => (a.friendlyName.toLowerCase() > b.friendlyName.toLowerCase() ? 1 : -1)),
		);
	}, []);

	const saveAzureApplication = React.useCallback(async () => {
		try {
			setSaving(true);
			const newAppConfig = await updateThirdPartyApplications(
				selectedConfig,
				appConfigs[selectedConfigId],
				THIRD_PARTY_APPLICATION_TYPES.AZURE,
				selectedConfigId || "NEW",
			);
			const newAppConfigs = { ...appConfigs, [selectedConfig.applicationId]: newAppConfig };
			if (selectedConfig.applicationId !== selectedConfigId) {
				delete newAppConfigs[selectedConfigId];
			}
			setAppConfigs(newAppConfigs);
			setSelectedConfigId(selectedConfig.applicationId);
			setSelectedConfig(newAppConfig);
			onSearchTermChange(searchTerm, newAppConfigs);
			setFormEdited(false);
			showSnackbar("Successfully saved application.", "success");
		} catch (error) {
			v2.error(`Error while saving updated third party application.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.UPDATE_THIRD_PARTY_APPLICATIONS_ERROR,
			});
			showSnackbar("Failed to save application.", "error");
		} finally {
			setSaving(false);
		}
	}, [
		appConfigs,
		setSaving,
		showSnackbar,
		selectedConfigId,
		selectedConfig,
		setFormEdited,
		searchTerm,
		onSearchTermChange,
	]);

	const cancelFunction = React.useCallback(() => {
		setSelectedConfig(appConfigs[selectedConfigId]);
		setFormEdited(false);
		setSaveDisabled(false);
	}, [appConfigs, selectedConfigId, setFormEdited]);

	const deleteFunction = React.useCallback(async () => {
		try {
			setSaving(true);
			await deleteThirdPartyApplication(selectedConfigId, appConfigs[selectedConfigId].platform);
			const newAppConfigs = { ...appConfigs };
			delete newAppConfigs[selectedConfigId];
			setAppConfigs(newAppConfigs);
			onSearchTermChange(searchTerm, newAppConfigs);
			showSnackbar("Successfully removed application.", "success");
		} catch (error) {
			v2.error(`Error while deleting third party application.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.DELETE_THIRD_PARTY_APPLICATIONS_ERROR,
			});
			showSnackbar("Failed to remove application.", "error");
		} finally {
			setSaving(false);
			setSelectedConfig(undefined);
			setSelectedConfigId(undefined);
			setFormEdited(false);
		}
	}, [appConfigs, selectedConfigId, showSnackbar, setSaving, onSearchTermChange, setFormEdited, searchTerm]);

	React.useEffect(() => {
		if (selectedConfigId) {
			window.scrollTo({
				top: 350,
				left: 0,
				behavior: "smooth",
			});
		}
	}, [selectedConfigId]);

	return (
		<>
			<LeftPanelLayout>
				<ApplicationList
					createButtonText={"New Application"}
					labelText={"Teams Applications"}
					emptyListText={"No Applications Found"}
					selectApplication={(newSelectedConfigId) => {
						setSelectedConfig({ ...appConfigs[newSelectedConfigId] });
						setSelectedConfigId(newSelectedConfigId);
						setFormEdited(false);
						setSaveDisabled(false);
					}}
					createNewApplication={() => {
						setSelectedConfig({
							applicationId: uuid(),
							secretId: uuid(),
							botId: "",
							password: "",
							contactFlowId: "",
							platform: THIRD_PARTY_APPLICATION_TYPES.AZURE,
							friendlyName: "New Application",
						});
						setSelectedConfigId(undefined);
						setSaveDisabled(false);
						setFormEdited(false);
					}}
					formEdited={formEdited}
					setFormEdited={setFormEdited}
					saving={saving}
					searchTermLabel={"Search by Bot ID or Friendly Name"}
					searchTerm={searchTerm}
					selectedConfigId={selectedConfigId}
					filteredAppConfigList={filteredAppConfigList}
					onSearchTermChange={(newSearchTerm) => onSearchTermChange(newSearchTerm, appConfigs)}
				/>
			</LeftPanelLayout>
			<RightPanelLayout>
				<Card style={{ width: "100%", minHeight: "600px" }}>
					{filteredAppConfigList && selectedConfig ? (
						<>
							<AzureIntegrationAppCard
								appSettings={{ ...selectedConfig }}
								deleteFunction={deleteFunction}
								updateAppSettings={(valueName, value) => {
									const newEditedConfigValueObject = {
										...selectedConfig,
										[valueName]: value,
									};
									const lowerCaseFriendlyName = newEditedConfigValueObject.friendlyName.toLowerCase();
									if (
										!newEditedConfigValueObject.applicationId ||
										!newEditedConfigValueObject.botId ||
										!newEditedConfigValueObject.friendlyName ||
										!newEditedConfigValueObject.contactFlowId ||
										!newEditedConfigValueObject.appSecret ||
										Object.values(appConfigs).find((val) => {
											return (
												val.friendlyName?.toLowerCase() === lowerCaseFriendlyName &&
												val.applicationId !== selectedConfigId
											);
										})
									) {
										setSaveDisabled(true);
									} else if (saveDisabled) {
										setSaveDisabled(false);
									}
									setSelectedConfig(newEditedConfigValueObject);
								}}
								onSave={saveAzureApplication}
								onCancel={cancelFunction}
								saveDisabled={saveDisabled}
								saving={saving}
								formEdited={formEdited}
								setFormEdited={setFormEdited}
								contactFlows={contactFlows}
								appConfigs={appConfigs || {}}
								selectedAppId={selectedConfigId}
							/>
						</>
					) : (
						<EmptyPlaceholder message="To get started, select an application from the list on the left, or click the 'Add' button to create a new application" />
					)}
				</Card>
			</RightPanelLayout>
		</>
	);
};
